import { useQuery } from '@tanstack/react-query';

import { fetchModels, type ModelsResponse } from './aiClient';

/**
 * React Query hook for the AI model allowlist.
 *
 * Strategy: fetch once per app session, cache for 5 minutes. The
 * server already advertises Cache-Control: private, max-age=60 so
 * cross-tab + reload also benefit. We use staleTime: 5 minutes to
 * avoid refetching on every component mount; users rarely change the
 * allowlist mid-session.
 *
 * Both the Generate modal and the AI Chat panel use this hook so
 * the picker stays in sync — change the env-driven allowlist on the
 * server, refresh the tab, and both UIs pick up the new options.
 */
export function useModels() {
  return useQuery({
    queryKey: ['ai-models'],
    queryFn: fetchModels,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/* ----------------------------- Display helpers ---------------------------- */

/**
 * Helper used by both UIs: compare the picked model against what the
 * server indicates is its default, returning whether the pick will
 * cause a `modelSource: 'override'` on the next request. Useful for
 * a small "(override)" badge next to the picker.
 */
export function isOverride(meta: ModelsResponse['generate'] | undefined, picked: string | undefined): boolean {
  if (!meta || !picked) return false;
  return picked !== meta.default;
}
